import { BaseApiClient } from './base-client';
import type { ApiResponse, User } from '../../types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * User API - методы для работы с пользователем
 */
export class UserApi extends BaseApiClient {
  
  // ===== USER API =====
  
  async getUserInfo(): Promise<ApiResponse<User>> {
    const response = await this.client.get('/api/user/info');
    return response.data;
  }

  async updateUserTokens(tokens: number): Promise<ApiResponse<User>> {
    const response = await this.client.post('/api/user/tokens', { tokens });
    return response.data;
  }

  // ===== USER SETTINGS API =====
  
  async updateUserSettings(settings: {
    voice?: string;
    language?: string;
    theme?: string;
    preferredProvider?: string;
    preferredModel?: string;
    notificationsEnabled?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.put('/api/user/settings', settings);
    return response.data;
  }

  // ===== REFERRALS API =====
  
  /**
   * Get count of unviewed activated referrals
   */
  async getUnviewedReferralsCount(): Promise<ApiResponse<{ count: number }>> {
    const response = await this.client.get('/api/referrals/unviewed-count');
    return response.data;
  }

  /**
   * Mark all referrals as viewed
   */
  async markReferralsAsViewed(): Promise<ApiResponse<void>> {
    const response = await this.client.post('/api/referrals/mark-viewed');
    return response.data;
  }

  // ===== WELCOME PROMO API =====

  /**
   * Mark welcome modal as shown
   */
  async markWelcomeShown(): Promise<any> {
    const response = await this.client.post('/api/user/welcome-shown');
    return response.data;
  }

  // ===== USER INIT & PROFILE API =====

  /**
   * Initialize user (registration, referral tracking, campaign tracking)
   */
  async initUser(
    startParam?: string | null,
    sourceType?: string | null,
    utmData?: Record<string, string> | null
  ): Promise<any> {
    const response = await this.client.post('/api/user/init', {
      start_param: startParam || null,
      source_type: sourceType || null,
      utm_data: utmData || null,
    });
    return response.data;
  }

  /**
   * Record a campaign link click (public, no auth required)
   */
  async recordCampaignClick(
    campaignCode: string,
    sourceType: string = 'website',
    utmData?: Record<string, string> | null,
    fingerprint?: string | null
  ): Promise<any> {
    const response = await this.client.post('/api/campaigns/click', {
      campaign_code: campaignCode,
      source_type: sourceType,
      utm_data: utmData || null,
      fingerprint: fingerprint || null,
    });
    return response.data;
  }

  /**
   * Get user profile with subscription and settings
   */
  async getUserProfile(forceRefresh?: boolean): Promise<any> {
    const headers: Record<string, string> = {};
    if (forceRefresh) {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    }
    const response = await this.client.get('/api/user/profile', { headers });
    return response.data;
  }

  /**
   * Get subscription limits and usage
   */
  async getSubscriptionLimits(rawTeamId?: string): Promise<any> {
    const teamId = sanitizeTeamId(rawTeamId);
    const params: Record<string, string> = {};
    if (teamId) params.teamId = teamId;
    const response = await this.client.get('/api/user/limits', { params });
    return response.data;
  }

  /**
   * Get user notifications
   */
  async getUserNotifications(): Promise<any> {
    const response = await this.client.get('/api/user/notifications');
    return response.data;
  }

  /**
   * Get current Free Generations bonus pool snapshot.
   * Returns { success: true, pool: null } if user has no active pool (Free/Basic, expired, etc.).
   */
  async getBonusPoolStatus(): Promise<any> {
    const response = await this.client.get('/api/user/bonus-pool/status');
    return response.data;
  }

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId: string): Promise<any> {
    const response = await this.client.post(`/api/user/notifications/${notificationId}/read`);
    return response.data;
  }

  /**
   * Get user statistics
   */
  async getUserStats(days?: number): Promise<any> {
    const params: Record<string, any> = {};
    if (days) params.days = days;
    const response = await this.client.get('/api/user/stats', { params });
    return response.data;
  }

  /**
   * Get usage stats (token spending by models and categories)
   */
  async getUsageStats(from: string, to: string, category: string = 'all'): Promise<any> {
    const response = await this.client.get('/api/user/usage-stats', {
      params: { from, to, category }
    });
    return response.data;
  }

  /**
   * Get referral code/link
   */
  async getReferralCode(): Promise<any> {
    const response = await this.client.get('/api/referrals/code');
    return response.data;
  }

  /**
   * Get referral statistics
   */
  async getReferralStats(): Promise<any> {
    const response = await this.client.get('/api/referrals/stats');
    return response.data;
  }

  /**
   * Get referral history (list of invited users)
   */
  async getReferralHistory(): Promise<any> {
    const response = await this.client.get('/api/referrals/history');
    return response.data;
  }

  /**
   * Send referral invite via Telegram
   */
  async sendReferralInvite(): Promise<any> {
    const response = await this.client.post('/api/referrals/send-invite');
    return response.data;
  }

  /**
   * Claim accumulated referral tokens
   */
  async claimReferralTokens(): Promise<any> {
    const response = await this.client.post('/api/referrals/claim');
    return response.data;
  }

  /**
   * Get referrer's photo URL
   */
  async getReferrerPhoto(referrerId: string): Promise<any> {
    const response = await this.client.get(`/api/user/referrer-photo/${referrerId}`);
    return response.data;
  }

  // ===== USER MEMORY API (cross-chat persistent context) =====

  async getMemory(): Promise<ApiResponse<{
    memory_text: string;
    display_name: string | null;
    creative_profile: Record<string, unknown>;
    auto_extract_enabled: boolean | null;
    version: number;
    updated_at: string | null;
    exists: boolean;
    feature_allowed: boolean;
    limits: { memory_text: number; display_name: number };
  }>> {
    const response = await this.client.get('/api/user/memory');
    return response.data;
  }

  async updateMemory(payload: {
    memory_text?: string;
    display_name?: string | null;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.put('/api/user/memory', payload);
    return response.data;
  }

  async setMemoryAutoExtract(enabled: boolean): Promise<ApiResponse<any>> {
    const response = await this.client.patch('/api/user/memory/auto-extract', { enabled });
    return response.data;
  }

  async clearMemory(): Promise<ApiResponse<any>> {
    const response = await this.client.delete('/api/user/memory');
    return response.data;
  }
}

